import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, isFunction } from 'lodash/lang';

import PatientInfoPageApi from '../../../../../../../api/patient/PatientInfoPageApi';
import Button from '../../../../../../../common/general/Button';
import ButtonGroup from '../../../../../../../common/general/ButtonGroup';
import { onRequestError } from '../../../../../../../services/handlers';
import { useCurrentRoute } from '../../../../../../root/router';
import { usePatientInfo } from '../../PatientInfoContext';
import { TAB_CANCELED, TAB_PROJECTED } from '../useActiveTab';

import EachEncounter from './EachEncounter/EachEncounter';
import EachProjectedEpoch from './EachProjectedEpoch/EachProjectedEpoch';
import EncountersTabLoader from './EncountersTabLoader/EncountersTabLoader';
import useTabState from './useTabState';

import './EncountersTab.scss';

const DEFAULT_PAGE = 0;
const PAGE_SIZE = 10;

export default function EncountersTab({ activeTab }) {
  const currentRoute = useCurrentRoute();
  const ssuPatientId = currentRoute.params?.ssuPatientId;

  const { tabState, setTabState, concatMoreEncounters } = useTabState();
  const { epochs, encounters, page, totalEncounters } = tabState;
  const encounterLength = encounters?.length;

  const [loading, setLoading] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  const { getNewAndCompletedIcfForms } = usePatientInfo();

  const loadMoreIsAvailable = useMemo(
    function() {
      return encounterLength && encounterLength < totalEncounters;
    },
    [encounterLength, totalEncounters]
  );

  const loadPatientEncountersWithAppointments = useCallback(
    function(cb, page = DEFAULT_PAGE) {
      let canceled = false;
      const loadingStartTime = +new Date();

      dataFetcher(ssuPatientId, activeTab, page)
        .then(function({ data }) {
          if (canceled) {
            return;
          }
          const loadingEndTime = +new Date();
          const loadingDeltaTime = loadingEndTime - loadingStartTime;
          setTimeout(function() {
            if (canceled) {
              return;
            }
            cb(data);
          }, Math.max(700 - loadingDeltaTime, 0));
        })
        .catch(err => onRequestError(err, { customMessage: 'Getting Patient Encounters failed' }));
      return function() {
        canceled = true;
      };
    },
    [ssuPatientId, activeTab]
  );

  const loadMore = useCallback(
    function() {
      setLoadMoreLoading(true);
      loadPatientEncountersWithAppointments(function(data) {
        concatMoreEncounters(data);
        setLoadMoreLoading(false);
      }, page + 1);
    },
    [page, loadPatientEncountersWithAppointments, concatMoreEncounters]
  );

  const updatePatientEncountersWithAppointments = useCallback(
    function() {
      setLoading(true);
      return loadPatientEncountersWithAppointments(function(data) {
        setTabState(data);
        setLoading(false);
      }, DEFAULT_PAGE);
    },
    [loadPatientEncountersWithAppointments, setTabState]
  );

  useEffect(
    function() {
      return updatePatientEncountersWithAppointments();
    },
    [updatePatientEncountersWithAppointments]
  );

  return (
    <div className="encounters-section-content">
      <NoDataWrapper loading={loading} tabState={tabState}>
        {function() {
          return (
            <>
              {activeTab === TAB_PROJECTED
                ? epochs.map(function(epoch) {
                    return <EachProjectedEpoch epoch={epoch} key={epoch.epochId} />;
                  })
                : encounters?.map(function(encounter, key) {
                    return (
                      <EachEncounter
                        key={key}
                        encounter={encounter}
                        refetchTabData={() => {
                          updatePatientEncountersWithAppointments();
                          getNewAndCompletedIcfForms();
                        }}
                      />
                    );
                  })}
              {loadMoreLoading ? (
                <EncountersTabLoader />
              ) : (
                activeTab !== TAB_PROJECTED && (
                  <ButtonGroup style={{ justifyContent: 'center' }}>
                    <Button disabled={!loadMoreIsAvailable} onClick={loadMore} priority="low">
                      {loadMoreIsAvailable ? 'View More' : 'End of results'}
                    </Button>
                  </ButtonGroup>
                )
              )}
            </>
          );
        }}
      </NoDataWrapper>
    </div>
  );
}

function NoDataWrapper({ loading, tabState, children }) {
  if (loading) {
    return <EncountersTabLoader />;
  }
  const { encounters, epochs } = tabState;
  if (isEmpty(encounters) && isEmpty(epochs)) {
    return <div className="no-data-yet">There is no data yet</div>;
  }
  if (isFunction(children)) {
    return children();
  }
  return children;
}

function dataFetcher(ssuPatientId, activeTab, page = 0) {
  if (activeTab === TAB_PROJECTED) {
    return PatientInfoPageApi.getPatientProjectedEncounters(ssuPatientId);
  }
  return pageableFetcher(ssuPatientId, activeTab, page);
}

function pageableFetcher(ssuPatientId, activeTab, page) {
  return PatientInfoPageApi[
    activeTab === TAB_CANCELED
      ? 'getPatientCanceledEncountersWithAppointments'
      : 'getPatientActiveEncountersWithAppointments'
  ](ssuPatientId, page, PAGE_SIZE);
}
